import { Directive, inject, Inject, Input, Optional } from '@angular/core';
import { zip } from '@app/shared/util/array-utils';
import {
  AttributeStateService,
  BusinessItem,
  CoreConfig,
  CoreConfigService,
  DataSource,
  FilterViewProviderDirective,
  Item,
  ListView,
  ListViewProviderDirective,
  NtapGlobalConstants,
  Util,
  ViewData,
  ViewResultListener
} from '@ntap/core';
import { TreeNode } from 'primeng/api';
import {
  BehaviorSubject,
  first,
  forkJoin,
  iif,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap
} from 'rxjs';
import { DataItem, ListUtils } from './list-utils';

export type TreeNodeWithOriginalData = TreeNode & {
  originalData?: Item;
};

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[ntapHierarchyListViewProvider]',
  exportAs: 'ntapHierarchyListViewProvider',
  providers: [
    {
      provide: ListViewProviderDirective,
      useExisting: HierarchyListViewProviderDirective
    }
  ]
})
export class HierarchyListViewProviderDirective extends ListViewProviderDirective<ListView> {
  @Input() customSelectableFunction: (nodeData: any) => boolean = () => true;
  private _treeViewData$: Observable<TreeNode<Item>[]>;
  private explicitTreeViewData = new BehaviorSubject<TreeNode<Item>[]>([]);
  private idToNodeMap: Record<string, TreeNode> = {};
  private data: DataItem[] = [];
  private filterViewProvider = inject(FilterViewProviderDirective);

  constructor(
    @Optional() resultListener: ViewResultListener,
    dataSource: DataSource,
    @Inject(CoreConfigService) protected override coreConfig: CoreConfig,
    attributeStateService: AttributeStateService
  ) {
    super(resultListener, dataSource, coreConfig, attributeStateService);
    this._treeViewData$ = merge(
      this.explicitTreeViewData.asObservable(),
      this.fetchAndMapNodes()
    );
    this.addSubscription(
      this.viewData$.subscribe((viewData: ViewData) => {
        this.data = viewData as DataItem[];
      })
    );
  }

  @Input() nodeIcon: string | ((node: Item) => string);

  @Input() mapNodes: (
    response: Item[],
    parentNode: TreeNodeWithOriginalData | null,
    originalData: Item[]
  ) => TreeNodeWithOriginalData[] = (
    response: Item[],
    parentNode = null,
    originalData: Item[]
  ) => {
    const mapped = [];
    for (const [displayItem, originalItem] of zip([response, originalData])) {
      const node: TreeNodeWithOriginalData = {};
      node.data = displayItem;
      node.originalData = originalItem;
      node.children = [];
      node.leaf = false;
      node.selectable = this.customSelectableFunction(originalItem);
      if (this.nodeIcon) {
        node.icon = this.isText(this.nodeIcon)
          ? this.nodeIcon
          : this.nodeIcon(node);
      }
      if (parentNode) {
        node.parent = parentNode;
      }
      const mainType =
        displayItem[NtapGlobalConstants.ATTR_$itemType] ||
        displayItem[NtapGlobalConstants.ATTR_$mainType];
      node.key =
        displayItem[
          Util.getFullAttrName(mainType, NtapGlobalConstants.ATTR_id)
        ];

      if (node.key != undefined) {
        this.idToNodeMap[node.key] = node;
      }

      mapped.push(node);
    }
    return mapped;
  };

  private isText(data: unknown): data is string {
    return typeof data === 'string';
  }

  protected fetchAndMapNodes(): Observable<TreeNodeWithOriginalData[]> {
    return this.viewData$.pipe(
      switchMap((response) =>
        this.mapDataForDisplay(of(response)).pipe(
          map((display) => {
            return this.mapNodes(display, null, response);
          })
        )
      )
    );
  }

  // ovaj observable koriste Tree implementacije da bi dohvatile podatke
  get treeViewData$() {
    return this._treeViewData$;
  }

  nodeExpand(
    parentNode: TreeNodeWithOriginalData,
    currentNodes: TreeNodeWithOriginalData[]
  ): Observable<any> {
    return this.view$.pipe(
      first(),
      switchMap((view) => {
        const newState = this.mapNewState(view, parentNode);
        if (!newState) {
          throw new Error('Selected node with unknown id');
        }

        return this.dataSource.getViewData(this.viewName, newState).pipe(
          first(),
          switchMap((nodes) => {
            return this.mapDataForDisplay(of(nodes)).pipe(
              map((displayDataForNode) => {
                parentNode.children = this.mapNodes(
                  displayDataForNode,
                  parentNode,
                  nodes
                );
                this.explicitTreeViewData.next(currentNodes);
              })
            );
          })
        );
      })
    );
  }

  protected mapNewState(view: ListView, parentNode: TreeNodeWithOriginalData) {
    const viewState = this.viewState;
    const itemType =
      parentNode.data[NtapGlobalConstants.ATTR_$itemType] ||
      parentNode.data[NtapGlobalConstants.ATTR_$mainType];
    const hierarchyAttr = this.getHierarchyAttribute(view);
    const newState = {
      ...viewState,
      [hierarchyAttr['$fqan']]:
        parentNode.data[
          Util.getFullAttrName(itemType, NtapGlobalConstants.ATTR_id)
        ]
    };
    if (!newState) {
      throw new Error('Selected node with unknown id');
    }
    return newState;
  }

  private getHierarchyAttribute(view: ListView) {
    const hierarchyColumnId = (view as any)['hierarchy_column'];
    if (!hierarchyColumnId) {
      throw new Error(
        'Atribut veze hijerarhije nije definiran. Konfigurirajte vezu hijerarhije u konfiguraciji pogleda liste.'
      );
    }
    const hierarchyAttr = view.attributes
      .filter((attr) => attr.id === hierarchyColumnId)
      .pop();
    if (!hierarchyAttr) {
      throw new Error(
        `Atribut veze hijerarhije id = ${hierarchyColumnId} nije prisutan u pogledu. Dodajte atribut u konfiguraciju atributa pogleda liste.`
      );
    }
    return hierarchyAttr;
  }

  nodeCollapse(node: TreeNode) {
    if (node.children) {
      node.children.forEach((child: any) => delete child.$parentNode);
    }
    node.children = [];
  }

  override deleteItem(item: BusinessItem) {
    this.deleteTreeNodeChild(item.data).subscribe();
    // ne zovemo super jer ne zelimo refresh cijele liste
  }

  private deleteTreeNodeChild(item: any) {
    return this.view$.pipe(
      first(),
      map((view) => {
        const hierarchyAttr = this.getHierarchyAttribute(view);
        const parentNode = this.idToNodeMap[item[hierarchyAttr.$fqan]];
        const nodeChildren = (parentNode?.children as TreeNode[]) || [];
        const nodeToDeleteIndex = nodeChildren.findIndex(
          (child) => child.data.id == item.id
        );
        return iif(
          () => nodeToDeleteIndex != -1,
          of(nodeChildren.splice(nodeToDeleteIndex, 1)[0].data),
          of({})
        );
      })
    );
  }

  override deleteItemAtIndex(item: BusinessItem) {
    // TODO typeati BusinessItem kao TreeBusinessItem, da ne moramo castati
    return this.deleteTreeNodeChild(item.data);
  }

  override deleteSelectedItems() {
    // FIXME trebalo bi u frameworku promijeniti da delete vraca array poslanih i array brisanih
    // sa servera. Na taj nacin se moze extendati ponasanje bez kopiranja ovog koda
    return this.selectedItems$.pipe(
      first(),
      mergeMap((selectedItems) => {
        return this.dataSource
          .deleteViewData(
            this.viewName,
            this.transformItemsToBasicData([...selectedItems])
          )
          .pipe(
            mergeMap(() => {
              this.selectedItems = [];
              return forkJoin(
                selectedItems.map((item) => this.deleteTreeNodeChild(item))
              ).pipe(
                tap(() => {
                  this.explicitTreeViewData.next([
                    ...this.explicitTreeViewData.getValue()
                  ]);
                })
              );
            })
          );
      })
    );
  }

  // provjera dataKey a ne samo index iz tablice
  override updateItem(item: DataItem, index: number) {
    const newIndex = ListUtils.findMatchingIndex(this.data, item);
    // ako je dobar ili je novi
    if (index === newIndex) {
      super.updateItem(item, index);
    } //nema ga na stranici i nije novi
    else if (newIndex != -1) {
      super.updateItem(item, newIndex);
    }
  }
}
